import { FRPAction, FRPActions, FRPElement, FRPElementType, FRPMessage, FRPState, setSelectedElementIds, setShownElementsIds, setView } from "./FRPState";
import * as Turf from '@turf/turf';

export class FRPFunc {

  /**
   * Version where the focus is the property. (Not only fields)
   * @param feature 
   * @param nyProperty 
   * @param nyMatrikel 
   * @param nyMark 
   * @param nyMarkDel 
   */
  static FRPElemementFromFeature(
    feature: { geometry: any; properties: any },
  ): FRPElement {
    return {
      id: "NoId" + Math.random().toString(36).substring(2, 15),
      geometry: feature.geometry,
      type: FRPElementType.Element,
      status: "",
      documents: {},
      tags: [],
      refElementId: "",
      lastChangedUser: "",
      lastChangedTimestamp: new Date(),
    }
  }

  static FRPElementsFromFeatureCollection(geoJson: any, elementEdit?: (FRPElement: FRPElement, feature: { geometry: any; properties: any }) => FRPElement): FRPElement[] {
    if (geoJson.type === "Feature") {
      geoJson = Turf.featureCollection([geoJson]);
    }
    if (geoJson.type !== "FeatureCollection") {
      throw new Error("FeatureCollection expected");
    }
    let result: FRPElement[] = [];
    for (let ft of geoJson.features) {
      let element = this.FRPElemementFromFeature(ft);
      if (elementEdit) {
        element = elementEdit(element, ft);
      }
      if (this.isElement(element)) {
        result.push(element);
      } else {
        console.error("Invalid element structure");
      }
    }
    return result;
  }

  static isElement(element: unknown): element is FRPElement {
    return (
      typeof element === 'object' &&
      element !== null &&
      'id' in element && element.id !== undefined &&
      'type' in element && element.type === FRPElementType.Element &&
      'geometry' in element && "type" in (element.geometry as any) &&
      // 'documents' in element &&
      // 'lastChangedTimestamp' in element &&
      // 'lastChangedUser' in element &&
      'status' in element &&
      'tags' in element
    );
  }

  static frpHandleFilter(filter: { [key: string]: any[] } | undefined, frpState: FRPState): (item: FRPElement) => boolean {
    if (!filter || Object.keys(filter).length === 0) {
      return () => true; // No filter, return all items
    }
    return (item: FRPElement) => {
      for (const [key, values] of Object.entries(filter)) {
        let inferredItemValue: any;
        if (key === "status") {
          const { status: inferredStatus } = FRPFunc.determineNestedChildrenStatusWithSource(item, frpState);
          inferredItemValue = inferredStatus ?? "";
        } else if (key === "tags") {
          const parentAttr = FRPFunc.determineParentAttribute(item, frpState, key);
          inferredItemValue = parentAttr?.value ?? "";
        } else {
          inferredItemValue = item[key];
        }
        if (Array.isArray(inferredItemValue)) {
          if (!values.every(value => inferredItemValue.includes(value))) return false; // If the item does not match all values in the array, skip to next key
          continue; // If the item does not match any of the values, skip to next key
        } else if (!values.includes(inferredItemValue)) {
          return false;
        }
      }
      return true;
    };
  }

  static getAllDescendants(parentId: string, childrenLookupTable: { [key: string]: string[] }, visited: Set<string> = new Set()): string[] {
    if (visited.has(parentId)) {
      return [];
    }

    visited.add(parentId);

    const directChildren = childrenLookupTable[parentId] || [];
    const allDescendants = [...directChildren];

    directChildren.forEach(childId => {
      const childDescendants = this.getAllDescendants(childId, childrenLookupTable, visited);
      allDescendants.push(...childDescendants);
    });

    return allDescendants;
  }

  static filterAllDescendants(element: FRPElement, elementID: string | undefined, childrenLookupTable: { [key: string]: string[] }) {
    if (elementID === undefined) {
      return true;
    }

    const allDescendants = this.getAllDescendants(elementID, childrenLookupTable);
    return allDescendants.includes(element.id);
  }

  static filterByChildren(element: FRPElement, elementID: string | undefined, childrenLookupTable: { [key: string]: string[] }) {
    if (elementID === undefined) {
      return true;
    }

    const children = childrenLookupTable[elementID];

    return children?.includes(element.id);
  }

  static frpHandleFilterContent(filter: { [key: string]: any[] } | undefined, elements: FRPElement[], frpState: FRPState): (item: FRPMessage | FRPAction) => boolean {
    const elementFilter = FRPFunc.frpHandleFilter(filter, frpState);
    const elementID = elements.filter(elementFilter).map(e => e.id);
    if (!elementID || elementID.length === 0) {
      return () => true; // No elements match the filter, return all items
    }
    return (item: FRPMessage | FRPAction) => {
      if (item.elementId && !elementID.includes(item.elementId)) {
        return false;
      }
      if (!item.elementId && !elementID) {
        return true; // If the item does not have an elementId, skip it
      }
      return true;
    };
  }

  static determineParentAttribute<E extends keyof FRPElement>(element: FRPElement, frpState: FRPState, attribute: E, visited: Set<string> = new Set()): { value: FRPElement[E], elementId: string } | undefined {
    if (visited.has(element.id)) {
      return undefined;
    }

    if (element[attribute] !== undefined && element[attribute] !== "") {
      return { value: element[attribute], elementId: element.id };
    } else if (element.refElementId && element.refElementId !== "_various") {
      visited.add(element.id);

      const parentElement = frpState.elements[element.refElementId];
      if (parentElement) {
        return FRPFunc.determineParentAttribute(parentElement, frpState, attribute, visited);
      }
    }
    return undefined;
  }

  static allNestedChildrenIds(elementId: string, childrenLookupTable: { [key: string]: string[] }): string[] {
    let allChildren: string[] = [];
    let directChildren = childrenLookupTable[elementId] || [];
    allChildren.push(...directChildren);
    for (let childId of directChildren) {
      let nestedChildren = FRPFunc.allNestedChildrenIds(childId, childrenLookupTable);
      allChildren.push(...nestedChildren);
    }
    return allChildren;
  }



  static determineNestedChildrenStatusWithSource(element: FRPElement, frpState: FRPState): { status: string | undefined, elementId: string | undefined, elementIds: string[] } {
    const allChildrenIds = FRPFunc.allNestedChildrenIds(element.id, frpState.childrenLookupTable);
    const allChildrenLeafs = allChildrenIds.filter(id => !frpState.childrenLookupTable[id] || frpState.childrenLookupTable[id]?.length === 0);

    if (allChildrenLeafs.length === 0) {
      return {
        status: element.status || undefined,
        elementId: element.status ? element.id : undefined,
        elementIds: element.status ? [element.id] : []
      };
    }

    const undefinedSources: string[] = [];
    const childResults: { status: string, elementId: string }[] = [];

    for (const childId of allChildrenLeafs) {
      const child = frpState.elements[childId];
      if (child) {
        const childStatus = child.status || undefined;
        if (childStatus === undefined) {
          undefinedSources.push(childId);
        }
        else {
          childResults.push({
            status: childStatus,
            elementId: childId
          });
        }
      }
    }

    if (undefinedSources.length > 0) {
      return {
        status: undefined,
        elementId: undefinedSources[0],
        elementIds: undefinedSources
      };
    }

    if (childResults.length === 0) {
      return {
        status: undefined,
        elementId: undefined,
        elementIds: []
      };
    }

    let worstStatusId: string | undefined = undefined;
    let worstStatusValue: number = Infinity;
    let worstSources: string[] = [];

    for (const result of childResults) {
      if (result.status) {
        const statusOption = frpState.statusOptions.find(opt => opt.id === result.status);
        if (statusOption && statusOption.status < worstStatusValue) {
          worstStatusValue = statusOption.status;
          worstStatusId = result.status;
          worstSources = [result.elementId];
        } else if (statusOption && statusOption.status === worstStatusValue) {
          worstSources.push(result.elementId);
        }
      }
    }

    if (!worstStatusId) {
      return { status: undefined, elementId: undefined, elementIds: [] };
    }

    return { status: worstStatusId, elementId: worstSources[0], elementIds: worstSources };
  }

  static determineAllRootElements(frpState: FRPState): string[] {
    let rootElements = Object.values(frpState.elements).filter(el => (frpState.childrenLookupTable[el.id]?.length > 0 && el.refElementId === "") || (!frpState.childrenLookupTable[el.id] && el.refElementId === ""));
    return rootElements.map(el => el.id);
  }

  static getElementHierarchy(elementId: string, frpState: FRPState): string[] {
    const hierarchy: string[] = [elementId];
    let currentElementId = elementId;


    while (true) {
      const currentElement = frpState.elements[currentElementId];
      if (!currentElement || !currentElement.refElementId) {
        break;
      }

      const parentId = currentElement.refElementId;
      hierarchy.unshift(parentId);
      currentElementId = parentId;


      if (hierarchy.length > 100) {
        console.warn('Hierarchy chain too long, possible circular reference');
        break;
      }
    }

    return hierarchy;
  }
  static getElementProgressRecursive(elementId: string, depth: number = 0, frpState: FRPState): { status: number; count: number } {
    if (depth > 10) return { status: 0, count: 0 };

    const element = frpState.elements[elementId];
    if (!element) return { status: 0, count: 0 };

    if (element.status && !frpState.childrenLookupTable[element.id]) {
      const statusOption = frpState.statusOptions.find((a) => a.id === element.status);
      if (statusOption) {
        return { status: statusOption.status, count: 1 };
      }
    }

    const childrenIds = frpState.childrenLookupTable[elementId] || [];
    if (childrenIds.length === 0) {
      return { status: 0, count: 0 };
    }

    let totalStatus = 0;
    let totalCount = 0;

    for (const childId of childrenIds) {
      const childResult = FRPFunc.getElementProgressRecursive(childId, depth + 1, frpState);
      totalStatus += childResult.status;
      totalCount += 1;
    }

    if (totalCount > 0) {
      return { status: Math.round(totalStatus / totalCount), count: totalCount };
    }

    return { status: 0, count: 0 };
  }

  static goToElement(frpState: FRPState, frpDispatch: (action: FRPActions) => void, key?: string | string[], goBack: boolean = false) {
    if (key && typeof key === 'string') {
      key = [key] as string[];
    }


    let shownElementsIds: string[] = [];
    let selectedElementIds: string[] = [];
    if (goBack && key && frpState.selectedElementIds.toString() === frpState.shownElementsIds.toString() && frpState.childrenLookupTable[key[0]]?.length !== 1) {
      // We're at a Leaf node
      // We should therefor show more, but keep the current selection 
      key = [frpState.selectedElementIds[0]]
      selectedElementIds = (key ?? []) as string[];
      shownElementsIds = key && frpState.elements[key[0]]?.refElementId ? frpState.childrenLookupTable[frpState.elements[key[0]].refElementId!] ?? [] : [];
    } else if (goBack && key && frpState.shownElementsIds.includes(frpState.selectedElementIds[0])) {
      // we have selected something inside 
      // We should therefore keep the shown elements, but change the selection
      selectedElementIds = (key ?? []) as string[];
      shownElementsIds = selectedElementIds.flatMap(parentId => frpState.childrenLookupTable[parentId] ?? []);
    } else {
      // We're want to actually go back
      selectedElementIds = (key ?? []) as string[];
      shownElementsIds = FRPFunc.getParentElements(selectedElementIds, frpState).flatMap(parentId => frpState.childrenLookupTable[parentId] ?? []);
    }



    frpDispatch(setSelectedElementIds(selectedElementIds));
    frpDispatch(setShownElementsIds(shownElementsIds));

    if (selectedElementIds.length === 0 && shownElementsIds.length === 0) {
      frpDispatch(setView('project'));
    } else {
      frpDispatch(setView('element'));
    }
  }

  static getParentElements(elementIds: string[], frpState: FRPState): string[] {
    const parentIds = new Set<string>();

    for (const elementId of elementIds) {
      const element = frpState.elements[elementId];
      if (element?.refElementId && element.refElementId !== "" && element.refElementId !== "_various") {
        parentIds.add(element.refElementId);
      }
    }

    return Array.from(parentIds);
  }
}

